import React, { useEffect, useMemo } from 'react';

import { useSelector } from 'react-redux';

import { Link } from 'react-router-dom';

import config from 'app.config';
import { NPR_ERROR_TYPES } from 'connect/constants';
import { selectDataClinicId } from 'connect/selectors';

import { FormError } from 'pages/checkout/components/common/input-styled-components';
import { Clinic } from 'types/Clinic';

const PATIENT_APP_PATH = `${config.PATIENT_APP_URL}/#/appointments`;

interface ErrorMessageProps {
  requestAppointmentError: typeof NPR_ERROR_TYPES[keyof typeof NPR_ERROR_TYPES];
  validateSlotError?: string;
}

export const ErrorMessage = ({
  requestAppointmentError,
  validateSlotError,
}: ErrorMessageProps) => {
  const clinicId = useSelector<any, Clinic['id']>(selectDataClinicId);

  const errorMessage = useMemo(() => {
    if (!requestAppointmentError && !validateSlotError) {
      return null;
    }
    const CLINIC_PROFILE_PATH = `/results/${clinicId}`;
    const CHECKOUT_VERIFY_PATH = `${CLINIC_PROFILE_PATH}/checkout/verify`;

    // Note: this occurs when slot validation fails, NOT when the slot is already booked
    // That case is handled in Confirm.tsx
    if (validateSlotError) {
      return (
        <span>
          There was an issue with this practice's calendar, please select{' '}
          <Link to="/results">another practice.</Link>
        </span>
      );
    }

    return {
      [NPR_ERROR_TYPES.SLOT_TAKEN]: (
        <span>
          The selected time slot is already booked. Please <Link to={CLINIC_PROFILE_PATH}>select another time</Link>.
        </span>
      ),
      [NPR_ERROR_TYPES.APPOINTMENT_REQUESTED_ALREADY]: (
        <span>
          You have already submitted an appointment request with this
          practice. <a href={PATIENT_APP_PATH}>Click here</a> to manage your appointment.
        </span>
      ),
      [NPR_ERROR_TYPES.NOT_PATIENT]: (
        <span>
          You must be logged in as a patient to book an appointment.
          The email address entered is associated with a practice or provider.
        </span>
      ),
      [NPR_ERROR_TYPES.PROVIDER_NO_ONLINE_BOOKING]: (
        <span>
          This provider has disabled new appointment requests at the moment,
          please select <Link to="/results">another practice.</Link>
        </span>
      ),
      [NPR_ERROR_TYPES.PROVIDER_NO_RESERVED_SLOTS]: (
        <span>
          There was an issue with this practice's calendar, please select{' '}
          <Link to="/results">another practice.</Link>
        </span>
      ),
      [NPR_ERROR_TYPES.PHONE_NOT_VERIFIED]: (
        <span>
          Please <Link to={CHECKOUT_VERIFY_PATH}>verify your phone number</Link> to book an appointment.
        </span>
      ),
      [NPR_ERROR_TYPES.EMAIL_NOT_VERIFIED]: (
        <span>
          Please <Link to={CHECKOUT_VERIFY_PATH}>verify your email address</Link> to book an appointment.
        </span>
      ),
      [NPR_ERROR_TYPES.GENERIC]: (
        <span>
          Something went wrong while submitting your details. Please refresh the page and try again.
          If the problem persists, please reach out to our support team.
        </span>
      ),
    }[requestAppointmentError];
  }, [
    requestAppointmentError,
    validateSlotError,
    clinicId,
  ]);

  // Scroll to the bottom of the page when an error occurs to make it visible
  useEffect(() => {
    if (!errorMessage) {
      return;
    }

    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  }, [errorMessage]);

  if (!errorMessage) {
    return null;
  }

  return (
    <FormError>
      {errorMessage}
    </FormError>
  );
};
